import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { keyframes, styled } from '@mui/material';
import Loading from 'components/Loading';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { useTranslation } from 'react-i18next';
import ModalHeader from '../../../ui/Modals/ModalHeader';

const fadeInFrame = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

const fadeOutFrame = keyframes`
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
`;

const StyStatusText = styled(Typography, {
    name: 'StyStatusText',
    shouldForwardProp: (prop) => prop !== 'fadeInProp',
})<{ fadeInProp: boolean }>(({ fadeInProp }) => ({
    animation: fadeInProp
        ? `${fadeInFrame} 0.5s ease-in`
        : `${fadeOutFrame} 0.5s ease-out`,
}));

const StyLoadingBox = styled(Box, {
    name: 'StyLoadingBox',
})({
    width: '100%',
    height: '15em',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
});

type Props = {
    isLoading: boolean;
    headerTitle: string;
    isError: boolean;
    progressSteps: string[];
};

const HRCreationLoader = ({
    isLoading,
    headerTitle,
    isError,
    progressSteps,
}: Props) => {
    const { t } = useTranslation();
    const [status, setStatus] = useState('');
    const [shouldFadeIn, setShouldFadeIn] = useState(true);

    const fadeDuration = 500; // 0.5s
    const stepDuration = 3000; // 3s

    const handleStep = (stepIdx: number) => {
        setStatus(progressSteps[stepIdx]);
        setShouldFadeIn(true);

        setTimeout(() => {
            setShouldFadeIn(false);
        }, stepDuration - fadeDuration);

        setTimeout(() => {
            if (isLoading) {
                const nextStep = (stepIdx + 1) % progressSteps.length;
                handleStep(nextStep);
            }
        }, stepDuration);
    };

    useEffect(() => {
        if (isLoading) {
            handleStep(0);
        }
    }, [isLoading]);

    return (
        <>
            <ModalHeader title={headerTitle} />
            <StyLoadingBox>
                {isLoading ? (
                    <>
                        <Loading />
                        <StyStatusText
                            variant="body1"
                            display="block"
                            gutterBottom
                            fadeInProp={shouldFadeIn}
                        >
                            {status}
                        </StyStatusText>
                    </>
                ) : (
                    <>
                        {isError ? (
                            <ErrorIcon fontSize="large" color="error" />
                        ) : (
                            <CheckCircleIcon fontSize="large" color="success" />
                        )}
                        <StyStatusText
                            variant="body1"
                            display="block"
                            gutterBottom
                            color={isError ? 'error' : 'success'}
                            fadeInProp
                        >
                            {isError
                                ? t(`humanResources.creationError`)
                                : t(`humanResources.done`)}
                        </StyStatusText>
                    </>
                )}
            </StyLoadingBox>
        </>
    );
};

export default HRCreationLoader;
